import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Tooltip } from "flowbite-react";
import { IoMdInformationCircleOutline } from "react-icons/io";
import {
  CatalogImageOwner,
  useFloorplannerProductById,
} from "../../../../features/floorplan3d/catalog/catalogQueries";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import { editorStore } from "../../../../store/editorStore";
import {
  collectPlacedCatalogProducts,
  PlacedCatalogProduct,
  summarizePlacedCatalogPrices,
} from "../../../../features/floorplan3d/products/collectPlacedCatalogProducts";
import { isUserModelProductListId } from "../../../../features/floorplan3d/userModels/userModel3DCommerce";
import { ProductLibraryDetailModal } from "../../productLibrary/ProductLibraryDetailModal";
import { buildOrderPortalBasketUrl } from "../../buildOrderPortalBasketUrl";
import PanelSection from "../components/PanelSection";
import { CurrencySelector } from "../../../CurrencySelector";
import { useCurrencyRates } from "../../../../hooks/useCurrencyRates";
import {
  convertAmountToDisplayCurrency,
  convertSekToDisplayAmount,
  formatAmountInDisplayCurrency,
  formatDisplayPrice,
  formatSekExchangeRateNote,
} from "../../../../utils/currency";
import { isSafeHttpUrl } from "../../../../utils/httpUrl";

/** Official BONI “Edit product list” walkthrough (same as My Page tutorials). */
const PRODUCT_LIST_TUTORIAL_URL =
  "https://www.youtube.com/watch?v=RJOjak6ecbQ";

/**
 * Lazy-loads catalog product details for the Product List info control.
 * Fetch starts only after the user requests details for a specific productId.
 */
const ProductListDetailHost: React.FC<{
  productId: string;
  onClose: () => void;
}> = ({ productId, onClose }) => {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useFloorplannerProductById(
    productId,
    false,
  );
  const product = data?.productById as CatalogImageOwner | undefined;

  if (loading && !product) {
    return (
      <span className="sr-only" role="status">
        {t("common.loadingEllipsis")}
      </span>
    );
  }

  if (error && !product) {
    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-3"
        data-product-library-modal="true"
        onClick={onClose}
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void refetch();
          }}
          className="rounded-md bg-white px-4 py-3 text-sm text-red-700 underline underline-offset-2"
        >
          {t("productLibrary.loadError")}
        </button>
      </div>
    );
  }

  if (!product) return null;

  return <ProductLibraryDetailModal product={product} onClose={onClose} />;
};

/**
 * Catalog products currently placed in the plan — shown in the property panel
 * when nothing is selected. Uses only local store/dictionary data (no network)
 * so the floorplan stays responsive.
 *
 * Collapses automatically when entering 3D so the viewport stays usable;
 * expands again when returning to 2D. Users can still toggle manually.
 */
const ProductListSection: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const products = collectPlacedCatalogProducts(floorplannerStore);
  const priceSummary = summarizePlacedCatalogPrices(products);
  const totalPieces = products.reduce((sum, entry) => sum + entry.quantity, 0);
  const [open, setOpen] = useState(!editorStore.view3D);
  const [detailProductId, setDetailProductId] = useState<string | undefined>();
  const { ratesArray, base } = useCurrencyRates();
  const displayCurrency = floorplannerStore.currency;

  useEffect(() => {
    setOpen(!editorStore.view3D);
  }, [editorStore.view3D]);

  const productIdsKey = products.map((entry) => entry.productId).join("|");
  useEffect(() => {
    setDetailProductId(undefined);
  }, [productIdsKey]);

  const selectProductPlacements = (entry: PlacedCatalogProduct) => {
    editorStore.clearSelections();
    entry.symbolIds.forEach((symbolId) => {
      const symbol = floorplannerStore.symbolsMap.get(symbolId);
      if (!symbol) return;
      floorplannerStore.selectSymbol(symbolId);
      editorStore.addSelection(symbol);
    });
    entry.wallIds.forEach((wallId) => {
      const wall = floorplannerStore.wallsMap.get(wallId);
      if (!wall) return;
      floorplannerStore.selectWall(wallId);
      editorStore.addSelection(wall);
    });
  };

  const catalogBasketEntries = products.filter(
    (entry) =>
      entry.source !== "userModel" && !isUserModelProductListId(entry.productId),
  );
  const hasUserModelEntries = products.some(
    (entry) =>
      entry.source === "userModel" || isUserModelProductListId(entry.productId),
  );

  const userModelsBasketHint = hasUserModelEntries
    ? t("propertyPanel.productListUserModelsBasketHint")
    : undefined;

  const openBasket = (channel?: "NOGA" | "DIRECT") => {
    const url = buildOrderPortalBasketUrl(
      process.env.REACT_APP_CLIENT_URI,
      catalogBasketEntries.map((entry) => ({
        productId: entry.productId,
        quantity: entry.quantity,
      })),
      channel ? { channel } : undefined,
    );
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  /** Only http(s) — stamped plan JSON can carry arbitrary urlStore values. */
  const openStoreUrl = (storeUrl: string) => {
    const trimmed = storeUrl.trim();
    if (!isSafeHttpUrl(trimmed)) return;
    window.open(trimmed, "_blank", "noopener,noreferrer");
  };

  /**
   * Catalog rows are SEK → project display currency.
   * User 3D models convert from their stored currency → project display currency.
   */
  const formatCatalogPrice = (amountSek: number) =>
    formatDisplayPrice(
      amountSek,
      displayCurrency,
      ratesArray,
      base,
      i18n.language,
    );

  const formatUserModelPrice = (amount: number, fromCurrency?: string) =>
    formatAmountInDisplayCurrency(
      amount,
      fromCurrency || "SEK",
      displayCurrency,
      ratesArray,
      base,
      i18n.language,
    );

  const isUserModelEntry = (entry: PlacedCatalogProduct) =>
    entry.source === "userModel" || isUserModelProductListId(entry.productId);

  let catalogTotalSek = 0;
  let userModelTotalDisplay = 0;
  let hasUserModelPrice = false;
  /** Cross-currency user-model lines skipped when FX rates are unavailable. */
  let unconvertedUserModelCount = 0;
  for (const entry of products) {
    if (typeof entry.price !== "number" || !Number.isFinite(entry.price)) {
      continue;
    }
    const line = entry.price * entry.quantity;
    if (isUserModelEntry(entry)) {
      const converted = convertAmountToDisplayCurrency(
        line,
        entry.currency || "SEK",
        displayCurrency,
        ratesArray,
        base,
      );
      // Never fall back to the raw amount — that would mix e.g. EUR into a SEK total.
      if (converted == null) {
        unconvertedUserModelCount += 1;
        continue;
      }
      userModelTotalDisplay += converted;
      hasUserModelPrice = true;
    } else {
      catalogTotalSek += line;
    }
  }

  const convertedCatalogTotal = convertSekToDisplayAmount(
    catalogTotalSek,
    displayCurrency,
    ratesArray,
    base,
  );
  const combinedTotal = convertedCatalogTotal + userModelTotalDisplay;

  const formatDisplayAmount = (amount: number) =>
    formatAmountInDisplayCurrency(
      amount,
      displayCurrency,
      displayCurrency,
      ratesArray,
      base,
      i18n.language,
    );

  /** Catalog-only total (matches basket). Null when there is no priced catalog. */
  const formattedCatalogTotal =
    catalogTotalSek > 0 ? formatCatalogPrice(catalogTotalSek) : null;
  /** Own 3D only, when there is no catalog price to pair with. */
  const formattedOwnOnlyTotal =
    catalogTotalSek <= 0 && hasUserModelPrice
      ? formatDisplayAmount(userModelTotalDisplay)
      : null;
  /** Catalog + own 3D — shown as a second line when both exist. */
  const formattedTotalWithOwn =
    catalogTotalSek > 0 && hasUserModelPrice
      ? formatDisplayAmount(combinedTotal)
      : null;
  const formattedTotal =
    formattedCatalogTotal ?? formattedOwnOnlyTotal ?? null;

  const exchangeRateNote = formatSekExchangeRateNote(
    displayCurrency,
    ratesArray,
    base,
  );
  const hasAnyPrice = priceSummary.pricedQuantity > 0;
  const basketDisabled = catalogBasketEntries.length === 0;

  const basketActions = (
    <div className="flex gap-1.5">
      <button
        type="button"
        className="flex min-h-9 min-w-0 flex-1 items-center justify-center rounded-md border border-gray-800 bg-gray-800 px-1.5 py-1.5 text-center text-[11px] font-semibold leading-tight text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={basketDisabled}
        onClick={() => openBasket()}
      >
        {t("propertyPanel.addToBasket")}
      </button>
      <button
        type="button"
        className="flex min-h-9 min-w-0 flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-1.5 py-1.5 text-center text-[10px] font-medium leading-tight text-gray-800 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={basketDisabled}
        onClick={() => openBasket("NOGA")}
      >
        {t("propertyPanel.orderViaNoga")}
      </button>
    </div>
  );

  const title = (
    <div className="flex items-baseline gap-2">
      <span>{t("propertyPanel.productListTitle")}</span>
      {totalPieces > 0 && (
        <span className="rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-semibold text-white">
          {totalPieces}
        </span>
      )}
    </div>
  );

  return (
    <>
      <PanelSection
        title={title}
        collapsible
        open={open}
        onToggle={() => setOpen((prev) => !prev)}
        maxHeightClassName="max-h-[620px]"
        showDivider={false}
      >
        <div
          className="flex flex-col gap-3 px-3 pb-3"
          aria-label={t("propertyPanel.productListTitle")}
        >
          {products.length === 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-center text-xs leading-relaxed text-gray-500">
                {t("propertyPanel.productListEmpty")}
              </p>
              <button
                type="button"
                className="flex h-9 w-full items-center justify-center rounded-md border border-gray-800 bg-gray-800 px-2 text-[12px] font-semibold text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={floorplannerStore.isInitializing || floorplannerStore.lockedByOther}
                onClick={() => editorStore.requestOpenProductLibrary()}
              >
                {t("propertyPanel.findProducts")}
              </button>
            </div>
          ) : (
            <>
              <ul className="flex max-h-[280px] flex-col gap-1.5 overflow-y-auto">
                {products.map((entry) => {
                  const isUserModel = isUserModelEntry(entry);
                  const linePrice =
                    typeof entry.price === "number"
                      ? isUserModel
                        ? formatUserModelPrice(
                            entry.price * entry.quantity,
                            entry.currency,
                          )
                        : formatCatalogPrice(entry.price * entry.quantity)
                      : null;
                  return (
                    <li key={entry.productId}>
                      <div
                        className={`rounded-md p-1.5 transition ${
                          isUserModel
                            ? "bg-[#E4E2DD] hover:bg-[#DAD7D1]"
                            : "bg-[#F7F6F4] hover:bg-[#F0EEEA]"
                        }`}
                      >
                        <div className="flex w-full items-center gap-0.5">
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-2 rounded-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-800"
                            onClick={() => selectProductPlacements(entry)}
                          >
                            <div className="h-9 w-9 shrink-0 overflow-hidden rounded bg-white">
                              {entry.imageUrl ? (
                                <img
                                  src={entry.imageUrl}
                                  alt=""
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                  decoding="async"
                                  // User-model thumbnails are auth-gated API paths.
                                  {...(isUserModel
                                    ? { crossOrigin: "use-credentials" as const }
                                    : {})}
                                />
                              ) : (
                                <div className="flex h-full items-center justify-center text-[8px] text-gray-400">
                                  —
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[10px] font-medium tracking-wide text-[#5a5752]">
                                {entry.brand ||
                                  entry.supplierName ||
                                  (isUserModel
                                    ? t("propertyPanel.productListUserModel")
                                    : t("propertyPanel.productListCatalog"))}
                              </p>
                              <p
                                className="truncate text-[11px] font-medium text-[#111]"
                                title={entry.name}
                              >
                                {entry.name}
                              </p>
                              {linePrice && (
                                <p className="truncate text-[10px] text-gray-500">
                                  {linePrice}
                                </p>
                              )}
                            </div>
                            {entry.quantity > 1 && (
                              <span className="shrink-0 rounded-full bg-gray-800 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                ×{entry.quantity}
                              </span>
                            )}
                          </button>
                          {isUserModel ? (
                            entry.storeUrl &&
                            isSafeHttpUrl(entry.storeUrl) ? (
                              <button
                                type="button"
                                aria-label={t(
                                  "propertyPanel.productListViewInStore",
                                )}
                                title={t("propertyPanel.productListViewInStore")}
                                onClick={() => openStoreUrl(entry.storeUrl!)}
                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-[#E5E3DE] hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-800"
                              >
                                <IoMdInformationCircleOutline
                                  size={16}
                                  aria-hidden="true"
                                />
                              </button>
                            ) : null
                          ) : (
                            <button
                              type="button"
                              aria-label={t("productLibrary.productInfo")}
                              title={t("productLibrary.productInfo")}
                              onClick={() => setDetailProductId(entry.productId)}
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-[#E5E3DE] hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-800"
                            >
                              <IoMdInformationCircleOutline
                                size={16}
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="rounded-md border border-gray-200 bg-white px-2.5 py-2">
                {/**
                 * One primary total + currency toggle. When catalog and own 3D
                 * both have prices, show the combined figure first and catalog
                 * as a quiet secondary line (avoids two competing totals).
                 */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] leading-snug text-gray-500">
                      {formattedTotalWithOwn
                        ? t("propertyPanel.productListPriceSummaryWithOwn")
                        : formattedOwnOnlyTotal
                          ? t("propertyPanel.productListPriceSummaryOwn")
                          : t("propertyPanel.productListPriceSummary")}
                    </p>
                    <p className="mt-0.5 whitespace-nowrap text-[15px] font-semibold tabular-nums text-gray-900">
                      {/*
                        Prefer a formatted total; if every priced line failed FX
                        conversion, still show a fallback — never an empty headline.
                      */}
                      {(formattedTotalWithOwn ?? formattedTotal) ??
                        t("propertyPanel.productListPriceUnavailable")}
                    </p>
                    {formattedTotalWithOwn && formattedCatalogTotal ? (
                      <p className="mt-1 text-[10px] leading-snug text-gray-500">
                        {t("propertyPanel.productListPriceSummaryCatalog")}:{" "}
                        <span className="tabular-nums text-gray-700">
                          {formattedCatalogTotal}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <CurrencySelector compact dense menuAlign="right" />
                </div>

                {(hasAnyPrice && catalogTotalSek > 0 && exchangeRateNote) ||
                (hasAnyPrice && priceSummary.missingPriceCount > 0) ||
                unconvertedUserModelCount > 0 ||
                (!hasAnyPrice && unconvertedUserModelCount === 0) ? (
                  <div className="mt-2 space-y-0.5 border-t border-gray-100 pt-1.5">
                    {hasAnyPrice && catalogTotalSek > 0 && exchangeRateNote && (
                      <p className="text-[10px] leading-snug text-gray-500">
                        {t("propertyPanel.productListExchangeRate", {
                          rate: exchangeRateNote,
                          defaultValue: "{{rate}} (indicative)",
                        })}
                      </p>
                    )}
                    {hasAnyPrice && priceSummary.missingPriceCount > 0 && (
                      <p className="text-[10px] leading-snug text-gray-500">
                        {t("propertyPanel.productListPricePartial", {
                          count: priceSummary.missingPriceCount,
                        })}
                      </p>
                    )}
                    {unconvertedUserModelCount > 0 && (
                      <p className="text-[10px] leading-snug text-gray-500">
                        {t("propertyPanel.productListPriceFxPartial", {
                          count: unconvertedUserModelCount,
                        })}
                      </p>
                    )}
                    {!hasAnyPrice && unconvertedUserModelCount === 0 && (
                      <p className="text-[10px] leading-snug text-gray-500">
                        {t("propertyPanel.productListPriceHint")}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                {hasUserModelEntries ? (
                  <Tooltip
                    content={userModelsBasketHint}
                    placement="top"
                    style="dark"
                    className="max-w-[220px] text-left text-[11px] leading-snug"
                  >
                    <span className="block w-full">{basketActions}</span>
                  </Tooltip>
                ) : (
                  basketActions
                )}
                <p className="px-0.5 text-center text-[10px] leading-snug text-gray-500">
                  {t("propertyPanel.productListPresentationHint")}{" "}
                  <a
                    href={PRODUCT_LIST_TUTORIAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-gray-800 underline underline-offset-2 hover:text-black"
                  >
                    {t("propertyPanel.productListTutorialLink")}
                  </a>
                </p>
              </div>
            </>
          )}
        </div>
      </PanelSection>

      {/* Keep selection hint outside the collapsible product list so it stays visible in 2D/3D. */}
      <div className="border-b border-gray-300" />
      <p className="px-3 py-3 text-center text-[10px] leading-relaxed text-gray-400">
        {t("propertyPanel.emptyHint")}
      </p>

      {detailProductId && (
        <ProductListDetailHost
          productId={detailProductId}
          onClose={() => setDetailProductId(undefined)}
        />
      )}
    </>
  );
});

export default ProductListSection;
